import type { QueryFunction } from "@tanstack/react-query";
import { queryKeys } from "@/constants/queryKeys";
import { TIME } from "@/constants/app";
import { API_BASE_URL } from "@/services/";

import type {
  UtilityBasicValuesResponse,
  UnlockScheduleResponse,
  BurnedResponse,
  LargeHoldersNetflowInflowOutflowResponse,
  HistoricalConcentrationResponse,
  AverageTimeTokenIsHeldResponse,
  FundingRoundsResponse,
  PublicTokenSalesResponse,
  InvestorsAndBackersResponse,
  TokenUnlocksResponse,
  TeamResponse,
} from "@/types/tokenDashboard/utilityTab";

export const fetchBasicValues: QueryFunction<
  UtilityBasicValuesResponse
> = async ({ queryKey }) => {
  const [, , tokenId] = queryKey;

  if (typeof tokenId !== "string") {
    throw new Error("tokenId must be a string");
  }

  const response = await fetch(
    `${API_BASE_URL}/tokenPagev2/utilityValue/basicValues/${tokenId.toUpperCase()}`
  );

  if (!response.ok) {
    throw new Error(`Failed to fetch basic values: ${response.status}`);
  }
  const res = response.json();
  return res;
};

export const BasicValuesQueryOptions = (tokenId: string) => ({
  queryKey: queryKeys.tokenPage.utilityValue.basicValues(tokenId),
  queryFn: fetchBasicValues,
  staleTime: 5 * TIME.MINUTE,
  gcTime: 10 * TIME.MINUTE,
  retry: 2,
});

// Allocation and Unlock Progress
export const fetchAllocationAndUnlockProgress: QueryFunction<
  TokenUnlocksResponse
> = async ({ queryKey }) => {
  const [, , tokenId] = queryKey;

  if (typeof tokenId !== "string") {
    throw new Error("tokenId must be a string");
  }

  const response = await fetch(
    `${API_BASE_URL}/tokenPagev2/utilityValue/allocationsAndNextUnlock/${tokenId.toUpperCase()}`
  );
  if (!response.ok)
    throw new Error(`Failed to fetch allocation: ${response.status}`);
  return response.json();
};
export const allocationAndUnlockProgressQueryOptions = (tokenId: string) => ({
  queryKey: queryKeys.tokenPage.utilityValue.allocation(tokenId),
  queryFn: fetchAllocationAndUnlockProgress,
  staleTime: 5 * TIME.MINUTE,
  gcTime: 10 * TIME.MINUTE,
  retry: 2,
});

// UnlockSchedule
export const fetchUnlockSchedule: QueryFunction<
  UnlockScheduleResponse
> = async ({ queryKey }) => {
  const [, , tokenId] = queryKey;

  if (typeof tokenId !== "string") {
    throw new Error("tokenId must be a string");
  }

  const response = await fetch(
    `${API_BASE_URL}/tokenPagev2/utilityValue/unlockSchedule/${tokenId.toUpperCase()}`
  );
  if (!response.ok)
    throw new Error(`Failed to fetch unlock schedule: ${response.status}`);
  return response.json();
};
export const unlockScheduleQueryOptions = (tokenId: string) => ({
  queryKey: queryKeys.tokenPage.utilityValue.unlockSchedule(tokenId),
  queryFn: fetchUnlockSchedule,
  staleTime: 5 * TIME.MINUTE,
  gcTime: 10 * TIME.MINUTE,
  retry: 2,
});

// Burned
export const fetchBurned: QueryFunction<BurnedResponse> = async ({
  queryKey,
}) => {
  const [, , tokenId] = queryKey;

  if (typeof tokenId !== "string") {
    throw new Error("tokenId must be a string");
  }

  const response = await fetch(
    `${API_BASE_URL}/tokenPagev2/utilityValue/burned/${tokenId.toUpperCase()}`
  );
  if (!response.ok)
    throw new Error(`Failed to fetch burned: ${response.status}`);
  return response.json();
};
export const burnedQueryOptions = (tokenId: string) => ({
  queryKey: queryKeys.tokenPage.utilityValue.burned(tokenId),
  queryFn: fetchBurned,
  staleTime: 5 * TIME.MINUTE,
  gcTime: 10 * TIME.MINUTE,
  retry: 2,
});

// LargeHoldersNetflowInflowOutflow
export const fetchLargeHoldersNetflowInflowOutflow: QueryFunction<
  LargeHoldersNetflowInflowOutflowResponse
> = async ({ queryKey }) => {
  const [, , tokenId] = queryKey;
  if (typeof tokenId !== "string") {
    throw new Error("tokenId must be a string");
  }

  const response = await fetch(
    `${API_BASE_URL}/tokenPagev2/utilityValue/largeHoldersNetflowInflowOutflow/${tokenId.toUpperCase()}`
  );
  if (!response.ok)
    throw new Error(
      `Failed to fetch large holders netflow inflow outflow: ${response.status}`
    );
  return response.json();
};
export const largeHoldersNetflowInflowOutflowQueryOptions = (tokenId: string) => ({
  queryKey: queryKeys.tokenPage.utilityValue.largeHoldersNetflowInflowOutflow(tokenId),
  queryFn: fetchLargeHoldersNetflowInflowOutflow,
  staleTime: 5 * TIME.MINUTE,
  gcTime: 10 * TIME.MINUTE,
  retry: 2,
});

// HistoricalConcentration
export const fetchHistoricalConcentration: QueryFunction<
  HistoricalConcentrationResponse
> = async ({ queryKey }) => {
  const [, , tokenId] = queryKey;
  if (typeof tokenId !== "string") {
    throw new Error("tokenId must be a string");
  }

  const response = await fetch(
    `${API_BASE_URL}/tokenPagev2/utilityValue/historicalConcentration/${tokenId.toUpperCase()}`
  );
  if (!response.ok)
    throw new Error(`Failed to fetch historical concentration: ${response.status}`);
  return response.json();
};
export const historicalConcentrationQueryOptions = (tokenId: string) => ({
  queryKey: queryKeys.tokenPage.utilityValue.historicalConcentration(tokenId),
  queryFn: fetchHistoricalConcentration,
  staleTime: 5 * TIME.MINUTE,
  gcTime: 10 * TIME.MINUTE,
  retry: 2,
});

// AverageTimeTokenIsHeld
export const fetchAverageTimeTokenIsHeld: QueryFunction<
  AverageTimeTokenIsHeldResponse
> = async ({ queryKey }) => {
  const [, , tokenId] = queryKey;
  if (typeof tokenId !== "string") {
    throw new Error("tokenId must be a string");
  }

  const response = await fetch(
    `${API_BASE_URL}/tokenPagev2/utilityValue/averageTimeTokenIsHeld/${tokenId.toUpperCase()}`
  );
  if (!response.ok)
    throw new Error(`Failed to fetch average time token is held: ${response.status}`);
  return response.json();
};
export const averageTimeTokenIsHeldQueryOptions = (tokenId: string) => ({
  queryKey: queryKeys.tokenPage.utilityValue.averageTimeTokenIsHeld(tokenId),
  queryFn: fetchAverageTimeTokenIsHeld,
  staleTime: 5 * TIME.MINUTE,
  gcTime: 10 * TIME.MINUTE,
  retry: 2,
});

// FundingRounds
export const fetchFundingRounds: QueryFunction<FundingRoundsResponse> = async ({
  queryKey,
}) => {
  const [, , tokenId] = queryKey;
  if (typeof tokenId !== "string") {
    throw new Error("tokenId must be a string");
  }

  const response = await fetch(
    `${API_BASE_URL}/tokenPagev2/utilityValue/fundingRounds/${tokenId.toUpperCase()}`
  );
  if (!response.ok)
    throw new Error(`Failed to fetch funding rounds: ${response.status}`);
  return response.json();
};
export const fundingRoundsQueryOptions = (tokenId: string) => ({
  queryKey: queryKeys.tokenPage.utilityValue.fundingRounds(tokenId),
  queryFn: fetchFundingRounds,
  staleTime: 5 * TIME.MINUTE,
  gcTime: 10 * TIME.MINUTE,
  retry: 2,
});

// PublicTokenSales
export const fetchPublicTokenSales: QueryFunction<PublicTokenSalesResponse> = async ({
  queryKey,
}) => {
  const [, , tokenId] = queryKey;
  if (typeof tokenId !== "string") {
    throw new Error("tokenId must be a string");
  }

  const response = await fetch(
    `${API_BASE_URL}/tokenPagev2/utilityValue/publicTokenSales/${tokenId.toUpperCase()}`
  );
  if (!response.ok)
    throw new Error(`Failed to fetch public token sales: ${response.status}`);
  return response.json();
};
export const publicTokenSalesQueryOptions = (tokenId: string) => ({
  queryKey: queryKeys.tokenPage.utilityValue.publicTokenSales(tokenId),
  queryFn: fetchPublicTokenSales,
  staleTime: 5 * TIME.MINUTE,
  gcTime: 10 * TIME.MINUTE,
  retry: 2,
});

// InvestorsAndBackers
export const fetchInvestorsAndBackers: QueryFunction<InvestorsAndBackersResponse> = async ({
  queryKey,
}) => {
  const [, , tokenId] = queryKey;
  if (typeof tokenId !== "string") {
    throw new Error("tokenId must be a string");
  }

  const response = await fetch(
    `${API_BASE_URL}/tokenPagev2/utilityValue/investorsAndBackers/${tokenId.toUpperCase()}`
  );
  if (!response.ok)
    throw new Error(`Failed to fetch investors and backers: ${response.status}`);
  return response.json();
};
export const investorsAndBackersQueryOptions = (tokenId: string) => ({
  queryKey: queryKeys.tokenPage.utilityValue.investorsAndBackers(tokenId),
  queryFn: fetchInvestorsAndBackers,
  staleTime: 5 * TIME.MINUTE,
  gcTime: 10 * TIME.MINUTE,
  retry: 2,
});

// Team
export const fetchTeam: QueryFunction<TeamResponse> = async ({ queryKey }) => {
  const [, , tokenId] = queryKey;
  if (typeof tokenId !== "string") {
    throw new Error("tokenId must be a string");
  }

  const response = await fetch(
    `${API_BASE_URL}/tokenPagev2/utilityValue/team/${tokenId.toUpperCase()}`
  );
  if (!response.ok) throw new Error(`Failed to fetch team: ${response.status}`);
  return response.json();
};
export const teamQueryOptions = (tokenId: string) => ({
  queryKey: queryKeys.tokenPage.utilityValue.team(tokenId),
  queryFn: fetchTeam,
  staleTime: 5 * TIME.MINUTE,
  gcTime: 10 * TIME.MINUTE,
  retry: 2,
});
